import {Component, OnInit, ViewChild, TemplateRef} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {take} from 'rxjs/operators';
import {ActivatedRoute, Router} from '@angular/router';
import {AuthOTPPayload} from 'luxtrust-cosi-api/model/authOTPPayload';
import {AuthenticationService} from 'luxtrust-cosi-api/api/authentication.service';
import {EnduserData} from 'luxtrust-cosi-api/model/enduserData';
import {InvitationPayload} from 'luxtrust-cosi-api/model/invitationPayload';
import {NotifierService} from 'src/app/services/services/notifier.service';
import {CONFIG, REGEXES, SESSION_STORAGE_SYSTEM_DOC_KEY} from '../../app.constant';
import {AlertService} from '../../services/services/alert-service';
import {AppService} from '../../services/services/app.service';
import {ValidatorHelper} from '../../services/validators/validators.helper';
import {ApiError} from '../../error/api-error.model';
import {ConfigurationService} from '../../services/services/configuration.service';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {AuthCodeError} from '../../services/enum/auth-code-error';
import {DocumentationService} from "../../admin/documentation/documentation.service";

@Component({
  templateUrl: './invite.component.html', styleUrls: ['./invite.component.scss']
})
export class InviteComponent implements OnInit {

  user: EnduserData;
  sessionId: number;
  stepId: number;
  registerForm: FormGroup;
  authCodeFormGroup: FormGroup;
  signInMandatory: boolean;
  authCodeChannel: string; // email or sms
  otpIsRequired: boolean = false;
  jwtWithOtp: string;

  @ViewChild('authCodeModal', { read: TemplateRef, static: false }) authCodeModal: TemplateRef<any>;

  constructor(private formBuilder: FormBuilder,
              private router: Router,
              private authenticationService: AuthenticationService,
              private alertService: AlertService,
              private appService: AppService,
              private route: ActivatedRoute,
              private configurationService: ConfigurationService,
              private notifierService: NotifierService,
              private modalService: NgbModal,
              private documentationService: DocumentationService) {
  }

  ngOnInit() {
    const jwt = this.route.snapshot.queryParams['jwt'];
    this.sessionId = parseInt(this.route.snapshot.queryParams['sessionId'], 10);
    this.stepId = parseInt(this.route.snapshot.queryParams['stepId'], 10);

    this.configurationService.tenantConfig.then(tenantConfig => {
      this.jwtWithOtp = tenantConfig[CONFIG.TENANT.AUTH.JWT_WITH_OTP_KEY];
      let tenantConfigElement = tenantConfig[CONFIG.TENANT.SIGNIN_MANDATORY.KEY];
      this.signInMandatory = tenantConfigElement != null;

      if (jwt) {
        if (this.jwtWithOtp) {
          this.otpIsRequired = true;
          this.loginWithJwt(jwt, this.jwtWithOtp, true, AuthOTPPayload.ActionEnum.GENERATEFROMURL);
        } else {
          this.appService.refresh(jwt);
          if (this.appService.isLoggedIn()) {
            this.appService.loadEnduser().then(user => {
              this.user = user;
              this.registerForm.get('firstName').patchValue(this.user.firstName);
              this.registerForm.get('lastName').patchValue(this.user.lastName);
              this.registerForm.get('email').patchValue(this.user.email);
            });
          }
        }
      };
    });

    this.registerForm = this.formBuilder.group({
      firstName: [{value: '', disabled: true}],
      lastName: [{value: '', disabled: true}],
      email: [{value: '', disabled: true}],
      password: ['',
        [
          Validators.required,
          Validators.pattern(REGEXES.PASSWORD.NUMERIC_CHARS),
          Validators.pattern(REGEXES.PASSWORD.SPECIAL_CHARS),
          Validators.pattern(REGEXES.PASSWORD.ALPHA_CASED_CHARS),
          Validators.minLength(6)
        ]
      ],
      confirmPassword: ['', [Validators.required, Validators.minLength(6)]]
    }, { validator: ValidatorHelper.matchPassword });

    this.authCodeFormGroup = this.formBuilder.group({
      authCode: ['', [Validators.required, Validators.pattern(REGEXES.AUTH_CODE)]]
    });

  }

  loginWithJwt(jwt: string, jwtWithOtp: string, openModal: boolean, action: AuthOTPPayload.ActionEnum) {
    const authOTPPayload: AuthOTPPayload = {action: action}
    this.appService.validateToken(jwt);
    this.authenticationService.authenticateWithOtp(authOTPPayload).pipe(take(1)).subscribe((isCodeSent: any) => {
      this.appService.setIsAuthenticated('false');
      const jsonValue = JSON.parse(jwtWithOtp);
      const channel = jsonValue['channel'];
      this.authCodeChannel = channel.toLowerCase();
      if (openModal) {
        this.modalService.open(this.authCodeModal, { ariaLabelledBy: 'modal-basic-title', backdrop: false });
      }
      if (isCodeSent === null || isCodeSent) {
        this.alertService.success('LOGIN.AUTH_CODE.ALERT_SENT');
      }
    }, (err) => {
      this.appService.removeTokenFromStorage();
    });
  }

  generateCode() {
    const jwt = this.route.snapshot.queryParams['jwt'];
    this.loginWithJwt(jwt, this.jwtWithOtp, false, AuthOTPPayload.ActionEnum.GENERATEFROMBTN);
  }

  onSubmit() {
    this.registerForm.markAllAsTouched();
    if (this.registerForm.invalid || this.otpIsRequired) {
      return;
    }

    const payload: InvitationPayload = {
      email: this.registerForm.value.email,
      password: this.registerForm.value.password || undefined,
      firstName: this.registerForm.value.firstName,
      lastName: this.registerForm.value.lastName
    };
    this.authenticationService.newUserInvitationSignup(payload).toPromise().then(() => {
      this.notifierService.notifyDetectUpdatePage('displayHome');
      if (!sessionStorage.getItem(SESSION_STORAGE_SYSTEM_DOC_KEY)) {
        this.documentationService.buildSystemDocumentation();
      }
      this.router.navigate(['/session', this.sessionId, 'step', this.stepId]);
    }).catch((error: ApiError) => {
      this.alertService.errorApi(error);
    });
  }

  onProceed() {
    if (!sessionStorage.getItem(SESSION_STORAGE_SYSTEM_DOC_KEY)) {
      this.documentationService.buildSystemDocumentation();
    }
    this.notifierService.notifyDetectUpdatePage('displayHome');
    this.router.navigate(['/session', this.sessionId, 'step', this.stepId]);
  }

  // Needed to display error immediately if any
  markAsTouched(controlName: string) {
    const control = this.registerForm.get(controlName);
    control.markAsTouched();
  }

  onAuthCodeSubmit() {
    const authCode: string = this.authCodeFormGroup.value.authCode.trim();
    const authOTPPayload: AuthOTPPayload = {
      action: AuthOTPPayload.ActionEnum.VERIFY,
      code: authCode,
      sessionId: this.sessionId,
      stepId: this.stepId
    }
    this.authenticationService.authenticateWithOtp(authOTPPayload).pipe(take(1)).subscribe((response: any) => {
      this.otpIsRequired = false;
      this.appService.refresh(response['access_token']);
      if (this.appService.isLoggedIn()) {
        if (!sessionStorage.getItem(SESSION_STORAGE_SYSTEM_DOC_KEY)) {
          this.documentationService.buildSystemDocumentation();
        }
        this.appService.loadEnduser().then(user => {
          this.user = user;
          this.registerForm.get('firstName').patchValue(this.user.firstName);
          this.registerForm.get('lastName').patchValue(this.user.lastName);
          this.registerForm.get('email').patchValue(this.user.email);
        });
      }
      this.onAuthCodeModalDismiss();
    }, (err) => {
      switch(err.error.code) {
        case AuthCodeError.AUTH_CODE_INCORRECT:
          this.alertService.error('LOGIN.AUTH_CODE.INCORRECT');
          break;
        case AuthCodeError.AUTH_CODE_LIMIT_EXCEEDED:
          this.alertService.error('LOGIN.AUTH_CODE.LIMIT_EXCEEDED');
          this.onAuthCodeModalDismiss();
          break;
        case AuthCodeError.AUTH_CODE_EXPIRED:
          this.alertService.error('LOGIN.AUTH_CODE.EXPIRED');
          break;
        default:
          this.alertService.error('ALERT.ERROR_OCCURRED');
      }
    });
  }

  onAuthCodeModalDismiss() {
    this.authCodeFormGroup.reset();
    this.modalService.dismissAll('normal');
  }

  updateAuthCode(val: EventListener) {
    if (!!val && val.length === 3) {
      this.authCodeFormGroup.setValue({authCode: val+' '});
    }
  }

}
